import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Barcode, Printer, MagnifyingGlass, ArrowClockwise, X, Check } from "@phosphor-icons/react";
import LabelPrintModal from "@/components/LabelPrintModal";

const inputCls = "w-full bg-transparent border-b py-2 outline-none focus:border-[#C2A878] text-sm";

function PageHeader({ eyebrow, title, subtitle, actions }) {
  return (
    <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
      <div>
        <div className="overline mb-3">{eyebrow}</div>
        <h1 className="font-serif font-light text-4xl sm:text-5xl tracking-tight leading-none">{title}</h1>
        {subtitle && <p className="mt-4 text-sm text-[#5C5A54] max-w-xl">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}

/* ═════════════ Assign Modal ═════════════ */
function AssignModal({ variant, onClose, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    barcode: variant?.barcode && variant.barcode !== variant.sku ? variant.barcode : "",
    barcode_type: variant?.barcode_type || "code128",
    reason: "",
  });
  const [conflict, setConflict] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    setConflict(null);
    try {
      const r = await api.post(`/variants/${variant.sku}/barcode`, form);
      toast.success(t("barcode.assigned"));
      onSaved(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.existing) {
        setConflict(detail.existing);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}
         data-testid="barcode-assign-modal">
      <div className="w-full max-w-md bg-white border shadow-xl" style={{ borderColor: "var(--nylsu-border-strong)" }}
           onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: "var(--nylsu-border)" }}>
          <div>
            <div className="overline">{t("barcode.assignTitle")}</div>
            <div className="text-xs text-[#5C5A54] mt-1">{variant.style_name} · {variant.color} · {variant.size}</div>
            <div className="text-[10px] font-mono text-[#8F8D84]">{variant.sku}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-[#8F8D84] hover:text-[#0A0A0A]"><X size={14} weight="light" /></button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="overline block mb-1">{t("barcode.type")}</label>
            <select className={inputCls} value={form.barcode_type} onChange={(e) => set("barcode_type", e.target.value)}
                    data-testid="barcode-assign-type">
              <option value="code128">Code 128</option>
              <option value="ean13">EAN-13</option>
            </select>
          </div>
          <div>
            <label className="overline block mb-1">{t("barcode.value")}</label>
            <input autoFocus className={inputCls} value={form.barcode} onChange={(e) => set("barcode", e.target.value)}
                   placeholder={form.barcode_type === "ean13" ? "8681234567890" : "e.g. INT-9F3AC1B7"}
                   data-testid="barcode-assign-value" />
          </div>
          <div>
            <label className="overline block mb-1">{t("barcode.reason")}</label>
            <input className={inputCls} value={form.reason} onChange={(e) => set("reason", e.target.value)}
                   placeholder={t("barcode.reasonPlaceholder")} data-testid="barcode-assign-reason" />
          </div>
          {conflict && (
            <div className="border p-3 text-xs" style={{ borderColor: "var(--nylsu-danger)", background: "#FDF3F3" }}
                 data-testid="barcode-assign-conflict">
              <div className="uppercase tracking-[0.15em] text-[10px]" style={{ color: "var(--nylsu-danger)" }}>
                {t("barcode.conflictTitle")}
              </div>
              <div className="mt-1 font-serif">{conflict.style_name}</div>
              <div className="text-[#5C5A54]">{conflict.color} · {conflict.size} · <span className="font-mono">{conflict.sku}</span></div>
            </div>
          )}
        </div>
        <div className="p-4 border-t flex justify-end gap-2" style={{ borderColor: "var(--nylsu-border)" }}>
          <button type="button" onClick={onClose} className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] border"
                  style={{ borderColor: "var(--nylsu-border)", color: "#5C5A54" }}>
            {t("common.cancel")}
          </button>
          <button type="button" onClick={save} disabled={saving || !form.barcode.trim()}
                  className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] border"
                  style={{ background: "var(--nylsu-gold)", borderColor: "var(--nylsu-gold)", color: "#0A0A0A" }}
                  data-testid="barcode-assign-save">
            {t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═════════════ Barcodes Page ═════════════ */
export default function BarcodesPage() {
  const { user } = useAuth();
  const { t, fmtCurrency } = useI18n();
  const canEdit = user?.role === "admin" || (user?.permissions || []).includes("barcode:edit");
  const canCreate = user?.role === "admin" || (user?.permissions || []).includes("barcode:create");
  const canBulk = user?.role === "admin" || (user?.permissions || []).includes("barcode:bulk_print");
  const canPrint = user?.role === "admin" || (user?.permissions || []).includes("barcode:print");

  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState({});
  const [qty, setQty] = useState({});
  const [assignFor, setAssignFor] = useState(null);
  const [printItems, setPrintItems] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/barcodes/variants", { params: q ? { q } : {} });
      setRows(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      (r.style_code || "").toLowerCase().includes(s) ||
      (r.style_name || "").toLowerCase().includes(s) ||
      (r.sku || "").toLowerCase().includes(s) ||
      (r.barcode || "").toLowerCase().includes(s) ||
      (r.color || "").toLowerCase().includes(s) ||
      (r.size || "").toLowerCase().includes(s)
    );
  }, [rows, q]);

  const toggle = (sku) => setSelected((s) => ({ ...s, [sku]: !s[sku] }));
  const setRowQty = (sku, v) => setQty((qs) => ({ ...qs, [sku]: Math.max(1, Math.min(200, Number(v || 1))) }));
  const anySelected = Object.values(selected).some(Boolean);
  const selectedItems = filtered
    .filter((r) => selected[r.sku])
    .map((r) => ({ ...r, qty: qty[r.sku] || 1 }));

  const generate = async (row) => {
    try {
      const overwrite = row.barcode && row.barcode !== row.sku;
      const r = await api.post(`/variants/${row.sku}/barcode/generate`, { overwrite });
      toast.success(t("barcode.generated"));
      updateRow(row.sku, r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.error === "existing_barcode") {
        if (window.confirm(t("barcode.overwriteConfirm", { current: detail.existing_barcode }))) {
          try {
            const r = await api.post(`/variants/${row.sku}/barcode/generate`, { overwrite: true });
            toast.success(t("barcode.generated"));
            updateRow(row.sku, r.data);
          } catch (e2) { toast.error(formatApiError(e2)); }
        }
      } else { toast.error(formatApiError(e)); }
    }
  };
  const deactivate = async (row) => {
    if (!window.confirm(t("barcode.deactivateConfirm"))) return;
    try {
      const r = await api.post(`/variants/${row.sku}/barcode/deactivate`);
      updateRow(row.sku, r.data);
      toast.success(t("barcode.deactivated"));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const updateRow = (sku, patch) => setRows((rs) => rs.map((r) => r.sku === sku ? { ...r, ...patch } : r));

  return (
    <div data-testid="barcodes-page">
      <PageHeader eyebrow={t("barcode.eyebrow")} title={t("barcode.title")} subtitle={t("barcode.subtitle")}
        actions={
          <div className="flex items-center gap-3">
            <button type="button" onClick={load}
              className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] border inline-flex items-center gap-2"
              style={{ borderColor: "var(--nylsu-border-strong)" }} data-testid="barcodes-refresh">
              <ArrowClockwise size={12} weight="light" /> {t("common.refresh")}
            </button>
            {canBulk && (
              <button type="button" disabled={!anySelected} onClick={() => setPrintItems(selectedItems)}
                className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] border inline-flex items-center gap-2 disabled:opacity-30"
                style={{ background: "var(--nylsu-gold)", borderColor: "var(--nylsu-gold)", color: "#0A0A0A" }}
                data-testid="barcodes-bulk-print">
                <Printer size={12} weight="light" /> {t("barcode.bulkPrint")} ({Object.values(selected).filter(Boolean).length})
              </button>
            )}
          </div>
        } />

      <div className="border" style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }}>
        <div className="p-4 border-b flex items-center gap-3" style={{ borderColor: "var(--nylsu-border)" }}>
          <MagnifyingGlass size={14} weight="light" className="text-[#8F8D84]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("barcode.searchPlaceholder")}
                 className="flex-1 bg-transparent outline-none text-sm" data-testid="barcodes-search-input" />
        </div>
        <div className="max-h-[70vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.15em]" style={{ background: "#F4F3ED" }}>
              <tr>
                <th className="px-3 py-3 text-left w-8"></th>
                <th className="px-3 py-3 text-left">{t("barcode.col.product")}</th>
                <th className="px-3 py-3 text-left">{t("barcode.col.color")}</th>
                <th className="px-3 py-3 text-left">{t("barcode.col.size")}</th>
                <th className="px-3 py-3 text-left">{t("barcode.col.sku")}</th>
                <th className="px-3 py-3 text-left">{t("barcode.col.barcode")}</th>
                <th className="px-3 py-3 text-left">{t("barcode.col.type")}</th>
                <th className="px-3 py-3 text-left">{t("barcode.col.status")}</th>
                <th className="px-3 py-3 text-right">{t("barcode.col.price")}</th>
                <th className="px-3 py-3 text-right">{t("barcode.col.labelsQty")}</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {busy && filtered.length === 0 && (
                <tr><td colSpan="11" className="px-3 py-6 text-center text-[#8F8D84]">…</td></tr>
              )}
              {!busy && filtered.length === 0 && (
                <tr><td colSpan="11" className="px-3 py-6 text-center text-[#8F8D84]">{t("common.empty")}</td></tr>
              )}
              {filtered.map((r) => {
                const isCustom = r.barcode && r.barcode !== r.sku;
                const inactive = r.barcode_status === "inactive";
                return (
                  <tr key={r.sku} className="border-t hover:bg-[#F4F3ED]" style={{ borderColor: "var(--nylsu-border)" }}
                      data-testid={`barcodes-row-${r.sku}`}>
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={!!selected[r.sku]} onChange={() => toggle(r.sku)}
                             data-testid={`barcodes-check-${r.sku}`} />
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-serif">{r.style_name}</div>
                      <div className="text-[10px] font-mono text-[#8F8D84]">{r.style_code}</div>
                    </td>
                    <td className="px-3 py-2">{r.color}</td>
                    <td className="px-3 py-2">{r.size}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.sku}</td>
                    <td className="px-3 py-2 font-mono text-xs" data-testid={`barcodes-value-${r.sku}`}>
                      {r.barcode || "—"}
                    </td>
                    <td className="px-3 py-2 uppercase text-[10px] tracking-[0.1em]">{r.barcode_type || "—"}</td>
                    <td className="px-3 py-2">
                      <span className="uppercase text-[10px] tracking-[0.1em] px-2 py-1 border"
                            style={{
                              borderColor: inactive ? "var(--nylsu-danger)" : (isCustom ? "var(--nylsu-gold)" : "var(--nylsu-border)"),
                              color: inactive ? "var(--nylsu-danger)" : (isCustom ? "var(--nylsu-gold)" : "#8F8D84"),
                            }}>
                        {inactive ? t("barcode.inactive") : (isCustom ? t("barcode.assigned") : t("barcode.default"))}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{fmtCurrency(r.retail_price, r.currency)}</td>
                    <td className="px-3 py-2 text-right">
                      <input type="number" min="1" max="200" value={qty[r.sku] || 1}
                             onChange={(e) => setRowQty(r.sku, e.target.value)}
                             className="w-14 border py-1 px-2 text-right font-mono text-xs"
                             style={{ borderColor: "var(--nylsu-border)" }}
                             data-testid={`barcodes-qty-${r.sku}`} />
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {canCreate && (
                        <button type="button" onClick={() => generate(r)}
                                className="text-[10px] uppercase tracking-[0.15em] underline text-[#5C5A54] hover:text-[#0A0A0A] mr-2"
                                data-testid={`barcodes-generate-${r.sku}`}>
                          {t("barcode.generate")}
                        </button>
                      )}
                      {canEdit && (
                        <button type="button" onClick={() => setAssignFor(r)}
                                className="text-[10px] uppercase tracking-[0.15em] underline text-[#5C5A54] hover:text-[#0A0A0A] mr-2"
                                data-testid={`barcodes-assign-${r.sku}`}>
                          {t("barcode.assign")}
                        </button>
                      )}
                      {canPrint && (
                        <button type="button" onClick={() => setPrintItems([{ ...r, qty: qty[r.sku] || 1 }])}
                                className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 border inline-flex items-center gap-1"
                                style={{ borderColor: "var(--nylsu-gold)", color: "var(--nylsu-gold)" }}
                                data-testid={`barcodes-print-${r.sku}`}>
                          <Printer size={11} weight="light" /> {t("barcode.print")}
                        </button>
                      )}
                      {canEdit && r.barcode_status !== "inactive" && isCustom && (
                        <button type="button" onClick={() => deactivate(r)}
                                className="text-[10px] uppercase tracking-[0.15em] underline text-[#8F8D84] hover:text-[#8C3B3B] ml-2"
                                data-testid={`barcodes-deactivate-${r.sku}`}>
                          {t("barcode.deactivate")}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {assignFor && (
        <AssignModal variant={assignFor} onClose={() => setAssignFor(null)}
                     onSaved={(patch) => { updateRow(assignFor.sku, patch); setAssignFor(null); }} />
      )}
      <LabelPrintModal open={!!printItems} items={printItems || []} onClose={() => setPrintItems(null)} />
    </div>
  );
}
